/**
 * =============================================================================
 * 【シンプル安定版 header-drawer.js】
 * =============================================================================
 *
 * Safari モバイルで「読み込みごとに表示が変わる」問題への対策。
 *
 * 原因:
 *   ES Module の読込が遅いと custom element 未定義のまま <details> だけ [open] になる。
 *   パネル表示が .menu-open 依存だと、その読み込みではメニューが永遠に見えない。
 *
 * 方針:
 *   1. 開閉状態の同期はこのファイルに集約する（header.liquid に依存しない）
 *   2. open / toggle / connectedCallback のどこからでも menu-open を補完する
 *   3. 閉じ処理は animationend 待ちに加え、Safari 向けタイムアウトを持つ
 *   4. スタッガーアニメの完了待ちに依存しない（CSS 側で無効化）
 */
import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

const CLOSE_FALLBACK_MS = 350;

/**
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {number | null} */
  #closeFallbackTimer = null;

  /** @type {boolean} */
  #isFinishingClose = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.refs.details.addEventListener('toggle', this.#onDetailsToggle);

    /* Module が遅延読込された場合でも、既に [open] なら menu-open を補完 */
    this.#syncMenuOpenFromDetails();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details.removeEventListener('toggle', this.#onDetailsToggle);
    this.#clearCloseFallback();
  }

  /** @param {KeyboardEvent} event */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Native <details> toggle: Safari ではここが open() より先／遅れることがある。
   * 開いたら menu-open を必ず付ける。
   */
  #onDetailsToggle = () => {
    this.#syncMenuOpenFromDetails();
  };

  #syncMenuOpenFromDetails() {
    const details = this.refs.details;
    const summary = details.querySelector('summary');
    const ariaExpanded = summary?.getAttribute('aria-expanded');

    if (details.open && ariaExpanded !== 'false') {
      details.classList.add('menu-open');
    }
  }

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /** @param {Event | undefined} event */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /** @param {Event} [event] */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;

    this.#clearCloseFallback();
    this.#isFinishingClose = false;
    summary.setAttribute('aria-expanded', 'true');

    /*
     * menu-open は即時付与する。
     * スタッガー無効化後は即時付与の方が Safari の遅延読込に強い。
     * toggle 同期 (#onDetailsToggle) が第二の保険。
     */
    details.classList.add('menu-open');
    trapFocus(details);
  }

  /** @param {Event} [event] */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  /** @param {HTMLDetailsElement} details */
  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.#isFinishingClose = false;

    const finish = () => {
      if (this.#isFinishingClose) return;
      this.#isFinishingClose = true;
      this.#clearCloseFallback();
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        this.querySelectorAll('details[open]').forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    };

    /* Safari: transition/animationend が来ないことがあるためタイムアウト併用 */
    this.#clearCloseFallback();
    this.#closeFallbackTimer = window.setTimeout(finish, CLOSE_FALLBACK_MS);

    onAnimationEnd(details, finish);
  }

  #clearCloseFallback() {
    if (this.#closeFallbackTimer != null) {
      clearTimeout(this.#closeFallbackTimer);
      this.#closeFallbackTimer = null;
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/** @param {HTMLDetailsElement} element */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
